/**
 * Take a number that represents the number of hours ahead or behind a timezone
 * is and formats it in a "-08:00" format.
 */
export const formatOffset = (
  offset: number
): string => {

  const modifier = Math.sign(offset) === -1 ? '-' : '+';
  const absOffset = Math.abs(offset);
  const offsetParts = String(absOffset).split('.');

  const minutes = offsetParts.length > 1 ? ':30' : ':00';
  let hours = "00";

  if (typeof offsetParts[0] === 'string') {
    hours = (Number(offsetParts[0]) > 9) ? String(offsetParts[0]) : `0${offsetParts[0]}`;
  }

  return modifier + hours + minutes;

};

/**
 * Takes an Iana Timezone and will generate the timezone offset (eg. -08:00)
 * by using Date.localeString, Date.UTC, and basic math.
 */
export const getIanaOffset = (
  ianaTimezone: string,
): number => {

  const now = new Date();
  now.setSeconds(0, 0);

  const localeString = now.toLocaleString('en-US', {
    timeZone: ianaTimezone,
    hourCycle: 'h23',
  });

  const match = /(\d+)\/(\d+)\/(\d+), (\d+):(\d+)/.exec(localeString);

  if (!match) {
    console.warn('Could not parse locale string for timezone offset.', localeString);
    return 0;
  }
  
  const [_, month, day, year, hour, min] = match.map(Number);

  if (typeof month !== 'number' || typeof day !== 'number' || typeof year !== 'number' || typeof hour !== 'number' || typeof min !== 'number') {
    console.warn('Could not parse locale string for timezone offset.', localeString);
    return 0;
  }

  const ianaUtcMilliseconds = Date.UTC(year, month - 1, day, hour, min);

  let offset = (Math.floor((ianaUtcMilliseconds - now.getTime()) / (1000 * 60))) / 60;

  return offset;

};

/**
 * Converts hours, minutes and seconds into milliseconds
 */
export const getMilliseconds = (
  hours: number,
  minutes: number,
  seconds: number,
): number => {

  return (hours * 60 * 60 + minutes * 60 + seconds) * 1000;

};

/**
 * Format a date string with a timezone offset for compatibility with symfony DateTimeInterface.
 * If no date is provided, the current date and time will be used.
 */
export const getDateTimeWithOffset = (
  ianaTimezone: string,
  dateTime: string | null = null,
): string => {

  const ianaOffset = getIanaOffset(ianaTimezone);
  const milliseconds = getMilliseconds(ianaOffset, 0, 0);

  const dateString = (dateTime === null)
    ? new Date(Date.now() + milliseconds).toISOString().slice(0, 16)
    : dateTime;

  return dateString + ":00" + formatOffset(ianaOffset);

};

/**
 * Takes a UTC delivery date and converts it to a date in the specified IANA timezone
 * -> @param utcToTimezone: true for UTC to specified timezone, false for specified timezone to UTC
 */
export const getDateTimeInTimezone = (
  dateString: string,
  ianaTimezone: string = "America/New_York",
  utcToTimezone: boolean = true,
): string => {

  const date = new Date(dateString + "Z");

  if (utcToTimezone) {

    // 1. UTC -> IANA Timezone
    return new Intl.DateTimeFormat("sv-SE", {
      timeZone: ianaTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date).replace(" ", "T");

  } else {

    // 2. IANA Timezone -> UTC
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: ianaTimezone,
      timeZoneName: "longOffset" 
    }).formatToParts(date);

    const offsetPart = parts.find(p => p.type === "timeZoneName")?.value || "GMT";
    const isoOffset = offsetPart.replace("GMT", "") || "Z";

    const utcDate = new Date(`${dateString}${isoOffset}`);
    return utcDate.toISOString().slice(0, 16);
  }

};

/**
 * Formats a date string (i.e. YYYY-MM-ddT00:00) into a human readable format.
 */
export const formatDatePretty = (
  date: string,
  includeTime: boolean = false,
) => {

  const locale = window.APP_ADMIN?.request?.params?.locale || 'en-US';

  const formattedDate = new Date(date).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });

  if (!includeTime) {
    return formattedDate;
  }

  const formattedTime = new Date(date).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  return `${formattedDate} ${formattedTime}`;

};

/**
 * Determines if a date string is within X days of the current date.
 */
export const isDateWithinXDays = (
  dateString: string, // YYYY-MM-DD HH:mm:ss.SSSSSS
  days: number,
) => {

  const inputDate = new Date(dateString.replace(" ", "T"));
  if (!inputDate || isNaN(inputDate.getTime())) {
    console.warn("Invalid date format");
    return false;
  }

  const currentDate = new Date();
  const targetDate = new Date(currentDate);
  targetDate.setDate(currentDate.getDate() - days);

  return inputDate >= targetDate;

};
